// PORTED FROM apps/web — see apps/web-employer/PORTING.md (or web-admin/) for follow-ups

import * as React from "react";
import { useNavigate } from "react-router-dom"; // TODO: useRouter → useNavigate (nav(x) → nav(x), router.replace(x) → nav(x, {replace:true}))
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import {
  Card,
  FilterSearch,
  FormField,
  Input,
  Spinner,
  useToast,
} from "@chotca/ui";
import { Check, ChevronDown } from "lucide-react";
import type { Gender } from "@chotca/config";
import { venueTypeFromBusinessType } from "@chotca/config";
import { useAdminOrgs } from "@chotca/api-client";
import { useOrg } from "@chotca/api-client";
import {
  useArchiveOrgAddress,
  useCreateOrgAddress,
  useOrgAddresses,
  useUpdateOrgAddress,
  useUploadAdminPostingImage,
} from "@chotca/api-client";
import { useAdminSystemPostingTemplates } from "@chotca/api-client";
import { getSdk, qk } from "@chotca/api-client";
import type {
  CreateOrgAddressRequest,
  UpdateOrgAddressRequest,
} from "@chotca/config";
import { postingToCreateValues } from "@chotca/posting-form";
import { PostingForm, type PostingFormOutput } from "@/components/posting-form";

export function NewPostingClient({
  initialOrgId,
  duplicateId,
}: {
  initialOrgId: string | null;
  // "Đăng lại": id of the posting to clone. We prefill the form + org from it.
  duplicateId?: string | null;
}) {
  const t = useTranslations("admin");
  const nav = useNavigate();
  const { toast } = useToast();
  const [orgId, setOrgId] = React.useState(initialOrgId ?? "");
  // Admin-only: provenance link (e.g. Facebook permalink) for a manually
  // entered posting. Employers never see this field.
  const [sourceUrl, setSourceUrl] = React.useState("");

  // "Đăng lại" (duplicate): fetch the source posting so we can pre-select its
  // org and prefill the create form — admin only reviews + tweaks, then posts.
  // We deliberately drop the source link so the clone doesn't collide with the
  // original on the Facebook-permalink dedupe; admin can paste a new one.
  const sourceQ = useQuery({
    queryKey: qk.admin.posting(duplicateId ?? ""),
    queryFn: () => getSdk().admin.getPosting(duplicateId!),
    enabled: !!duplicateId,
  });
  // Pre-select the source org exactly once — admin can still switch afterward.
  const appliedSourceOrg = React.useRef(false);
  React.useEffect(() => {
    if (!appliedSourceOrg.current && sourceQ.data?.orgId) {
      setOrgId(sourceQ.data.orgId);
      appliedSourceOrg.current = true;
    }
  }, [sourceQ.data?.orgId]);
  const selectedOrg = useOrg(orgId || null);
  const addressesQ = useOrgAddresses(orgId || null);
  const createAddress = useCreateOrgAddress(orgId);
  const updateAddress = useUpdateOrgAddress(orgId);
  const archiveAddress = useArchiveOrgAddress(orgId);
  const uploadPreview = useUploadAdminPostingImage(orgId);
  // Admin always sees system templates regardless of which org is selected —
  // they're admin-managed, org-agnostic. Loading them upfront makes "Bắt đầu
  // nhanh" clickable even before the OrgPicker is filled.
  const templates = useAdminSystemPostingTemplates();

  const toFormTemplate = (
    t: NonNullable<typeof templates.data>["items"][number],
  ) => ({
    id: t.id,
    name: t.name,
    label: t.label ?? null,
    position: t.position,
    venueType: t.venueType,
    employmentType: t.employmentType,
    title: t.title,
    description: t.description,
    salaryMin:
      typeof t.salaryMin === "number" ? t.salaryMin : Number(t.salaryMin),
    salaryMax:
      typeof t.salaryMax === "number" ? t.salaryMax : Number(t.salaryMax),
    salaryType: t.salaryType,
    defaultHeadcount: t.defaultHeadcount,
    benefits: t.benefits,
    skillsRequired: t.skillsRequired,
    skillsPreferred: t.skillsPreferred,
    requirements: t.requirements
      ? {
          gender:
            t.requirements.gender === "MALE" ||
            t.requirements.gender === "FEMALE"
              ? t.requirements.gender
              : null,
          minAge: t.requirements.minAge ?? null,
          maxAge: t.requirements.maxAge ?? null,
          minHeightCm: t.requirements.minHeightCm ?? null,
          motorbikeRequired: t.requirements.motorbikeRequired ?? false,
          excludeVisibleTattoo: t.requirements.excludeVisibleTattoo ?? false,
          kycCccdRequired: t.requirements.kycCccdRequired ?? false,
        }
      : undefined,
    softNotes: t.softNotes,
  });

  const create = useMutation({
    mutationFn: (values: PostingFormOutput) =>
      getSdk().admin.createPostingForOrg(orgId, {
        position: values.position,
        title: values.title,
        description: values.description,
        venueType: values.venueType,
        employmentType: values.employmentType,
        salaryMin: values.salaryMin,
        salaryMax: values.salaryMax,
        salaryType: values.salaryType,
        headcount: values.headcount,
        benefits: values.benefits,
        skillsRequired: values.skillsRequired,
        skillsPreferred: values.skillsPreferred,
        requirements: {
          gender: (values.reqGender ?? null) as Gender | null,
          minAge: values.reqMinAge ?? null,
          maxAge: values.reqMaxAge ?? null,
          minHeightCm: values.reqMinHeightCm ?? null,
          motorbikeRequired: values.reqMotorbike,
          excludeVisibleTattoo: values.reqExcludeTattoo,
          kycCccdRequired: values.reqKyc,
        },
        softNotes: values.softNotes || null,
        previewImageKey: values.previewImageKey ?? null,
        addressIds: values.addressIds,
        sourceUrl: sourceUrl.trim() || null,
      }),
    onSuccess: (p) => {
      toast({ tone: "success", title: "Đã đăng tin" });
      nav(`/postings/${p.id}`);
    },
    onError: () => toast({ tone: "error", title: "Đăng tin thất bại" }),
  });

  async function handleSubmit(values: PostingFormOutput) {
    if (!orgId) {
      toast({ tone: "error", title: "Vui lòng chọn doanh nghiệp" });
      return;
    }
    await create.mutateAsync(values);
  }

  const orgSlot = (
    <Card className="overflow-visible p-6">
      <div className="mb-4">
        <h3 className="text-base font-extrabold text-ink">
          {t("labelOrg")} <span className="text-danger">*</span>
        </h3>
        <p className="mt-1 text-xs text-ink/60">
          Chọn doanh nghiệp đăng tin trước khi nhập nội dung.
        </p>
      </div>
      <FormField
        label={t("labelOrg")}
        error={!orgId ? "Bắt buộc chọn doanh nghiệp" : undefined}
      >
        <OrgPicker
          orgId={orgId}
          onChange={setOrgId}
          placeholder={t("placeholderChoose")}
          shadowSuffix={t("orgShadowSuffix")}
          selectedLabel={selectedOrg.data?.name ?? null}
        />
      </FormField>
      {selectedOrg.data ? (
        <p className="mt-3 text-xs text-ink/70">
          Địa chỉ DN:{" "}
          <span className="font-medium text-ink">
            {selectedOrg.data.primaryAddress
              ? [
                  selectedOrg.data.primaryAddress.address,
                  selectedOrg.data.primaryAddress.district,
                  selectedOrg.data.primaryAddress.city,
                ]
                  .filter(Boolean)
                  .join(", ")
              : "—"}
          </span>
        </p>
      ) : null}
      {/* Admin-only provenance link. Lets re-scraped Facebook posts dedupe and
          gives admins a back-reference to the original source. */}
      <div className="mt-5 border-t border-smoke pt-4">
        <FormField
          label="Link nguồn (Facebook)"
          optional
          description="Chỉ admin thấy. Dán link bài đăng Facebook gốc nếu có."
        >
          <Input
            type="url"
            value={sourceUrl}
            onChange={(e) => setSourceUrl(e.target.value)}
            placeholder="https://facebook.com/..."
          />
        </FormField>
      </div>
    </Card>
  );

  // While the source loads (duplicate flow), show a skeleton so the form mounts
  // once with prefilled defaultValues rather than empty-then-reset.
  if (duplicateId && sourceQ.isPending) {
    return (
      <div className="space-y-6">
        <div className="h-10 w-2/3 animate-pulse rounded bg-ink/10" />
        <div className="h-64 animate-pulse rounded-xl bg-ink/5" />
      </div>
    );
  }

  const initialValues =
    duplicateId && sourceQ.data
      ? postingToCreateValues(sourceQ.data)
      : undefined;

  return (
    <PostingForm
      key={initialValues ? `dup-${duplicateId}` : "new"}
      initialValues={initialValues}
      loading={create.isPending}
      defaultVenueType={venueTypeFromBusinessType(
        selectedOrg.data?.businessType,
      )}
      onSubmit={handleSubmit}
      onUploadPreviewImage={
        orgId
          ? async (file, onProgress) => {
              const r = await uploadPreview.mutateAsync({ file, onProgress });
              return { key: r.key, url: r.publicUrl };
            }
          : undefined
      }
      orgSlot={orgSlot}
      submitLabel={t("submitPosting")}
      addressBundle={{
        addresses: addressesQ.data?.items ?? [],
        loading: orgId ? addressesQ.isLoading : false,
        onCreate: (input: CreateOrgAddressRequest) =>
          createAddress.mutateAsync(input),
        onUpdate: (id: string, input: UpdateOrgAddressRequest) =>
          updateAddress.mutateAsync({ id, body: input }),
        onArchive: async (id: string) => {
          await archiveAddress.mutateAsync(id);
        },
      }}
      savedTemplates={[]}
      systemTemplates={templates.data?.items.map(toFormTemplate) ?? []}
      manageTemplatesHref="/posting-templates"
      onManageTemplates={() => nav("/posting-templates")}
    />
  );
}

// ────────────────────────────────────────────────────────────────────────────
// OrgPicker — searchable combobox for admin (handles 10K+ orgs server-side)
// ────────────────────────────────────────────────────────────────────────────
function OrgPicker({
  orgId,
  onChange,
  placeholder,
  shadowSuffix,
  selectedLabel,
}: {
  orgId: string;
  onChange: (id: string) => void;
  placeholder: string;
  shadowSuffix: string;
  selectedLabel: string | null;
}) {
  const [open, setOpen] = React.useState(false);
  const [term, setTerm] = React.useState("");
  const [debounced, setDebounced] = React.useState("");
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const searchRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    const t = setTimeout(() => setDebounced(term.trim()), 220);
    return () => clearTimeout(t);
  }, [term]);

  const { data, isFetching } = useAdminOrgs(
    debounced ? { search: debounced } : {},
  );
  const items = data?.items ?? [];

  // Click-outside to close
  React.useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent) {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Auto-focus search input when opening
  React.useEffect(() => {
    if (open) {
      requestAnimationFrame(() => searchRef.current?.focus());
    } else {
      setTerm("");
    }
  }, [open]);

  const hasSelection = !!orgId && !!selectedLabel;

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`flex h-12 w-full items-center justify-between gap-2 rounded-[var(--radius-sm)] border-[1.5px] bg-mist px-4 text-left text-[15px] font-medium outline-none transition-colors focus:border-lime focus:bg-paper ${
          !orgId ? "border-danger" : "border-smoke"
        } ${open ? "border-lime bg-paper" : ""}`}
      >
        <span
          className={hasSelection ? "truncate text-ink" : "truncate text-ash"}
        >
          {hasSelection ? selectedLabel : placeholder}
        </span>
        <ChevronDown
          size={18}
          className={`shrink-0 text-ash transition-transform ${open ? "rotate-180" : ""}`}
          strokeWidth={2}
        />
      </button>

      {open ? (
        <div className="absolute left-0 right-0 top-[calc(100%+4px)] z-[60] overflow-hidden rounded-[var(--radius-sm)] border border-smoke bg-paper shadow-[0_12px_32px_rgba(15,15,15,0.12)]">
          <div className="border-b border-smoke p-2">
            <FilterSearch
              ref={searchRef}
              value={term}
              onChange={setTerm}
              placeholder="Tìm theo tên doanh nghiệp..."
              width="w-full"
            />
          </div>
          <div className="max-h-72 overflow-y-auto">
            {isFetching && items.length === 0 ? (
              <div className="flex items-center justify-center py-6">
                <Spinner size="sm" />
              </div>
            ) : items.length === 0 ? (
              <p className="px-4 py-6 text-center text-sm text-ink/60">
                {debounced
                  ? `Không tìm thấy doanh nghiệp khớp "${debounced}"`
                  : "Nhập tên để tìm doanh nghiệp"}
              </p>
            ) : (
              <ul role="listbox">
                {items.map((o) => {
                  const active = o.id === orgId;
                  return (
                    <li key={o.id}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={active}
                        onClick={() => {
                          onChange(o.id);
                          setOpen(false);
                        }}
                        className={`flex w-full items-start justify-between gap-3 px-4 py-2.5 text-left text-sm transition-colors hover:bg-mist ${
                          active ? "bg-lime/20" : ""
                        }`}
                      >
                        <div className="min-w-0">
                          <p className="truncate font-medium text-ink">
                            {o.name}
                            {o.isShadow ? (
                              <span className="ml-1 font-normal text-ash">
                                {shadowSuffix}
                              </span>
                            ) : null}
                          </p>
                          {o.primaryAddress ? (
                            <p className="mt-0.5 truncate text-xs text-ink/55">
                              {[
                                o.primaryAddress.address,
                                o.primaryAddress.district,
                                o.primaryAddress.city,
                              ]
                                .filter(Boolean)
                                .join(", ")}
                            </p>
                          ) : null}
                        </div>
                        {active ? (
                          <Check
                            size={16}
                            className="shrink-0 text-ink"
                            strokeWidth={2.5}
                          />
                        ) : null}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
            {items.length > 0 && isFetching ? (
              <div className="border-t border-smoke px-4 py-2 text-center text-xs text-ash">
                Đang cập nhật…
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
